import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
// Layouts : toujours présents dans le rendu, pas de lazy
import { PublicLayout } from './components/layout/PublicLayout';
import { ResponsableLayout } from './components/layout/ResponsableLayout';
import { EtudiantLayout } from './components/layout/EtudiantLayout';
import { EntrepriseLayout } from './components/layout/EntrepriseLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { CompteLayout } from './components/layout/CompteLayout';
import { RequireRole } from './components/auth/RequireRole';

// ─────────────────────────────────────────────────────────────────────────
// Pages — code splitting via React.lazy
// Les pages sont chargées à la demande, réduisant le bundle initial.
// ─────────────────────────────────────────────────────────────────────────

// Public
const LandingPage           = lazy(() => import('./pages/public/LandingPage').then(m => ({ default: m.LandingPage })));
const LoginPage             = lazy(() => import('./pages/public/LoginPage').then(m => ({ default: m.LoginPage })));
const CompanyRegisterPage   = lazy(() => import('./pages/public/CompanyRegisterPage').then(m => ({ default: m.CompanyRegisterPage })));
const RegisterCandidatePage = lazy(() => import('./pages/public/RegisterCandidatePage').then(m => ({ default: m.RegisterCandidatePage })));
const InvitationPage        = lazy(() => import('./pages/public/InvitationPage').then(m => ({ default: m.InvitationPage })));
const ForgotPasswordPage    = lazy(() => import('./pages/public/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage     = lazy(() => import('./pages/public/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const LegalPage             = lazy(() => import('./pages/public/LegalPage').then(m => ({ default: m.LegalPage })));
const NotFoundPage          = lazy(() => import('./pages/public/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const CandidatPublicPage    = lazy(() => import('./pages/public/CandidatPublicPage').then(m => ({ default: m.CandidatPublicPage })));
const ReferencePublicPage   = lazy(() => import('./pages/public/ReferencePublicPage').then(m => ({ default: m.ReferencePublicPage })));
const OrphanOfferClaimPage  = lazy(() => import('./pages/public/OrphanOfferClaimPage').then(m => ({ default: m.OrphanOfferClaimPage })));

// Responsable
const ResponsableDashboardPage      = lazy(() => import('./pages/responsable/DashboardPage').then(m => ({ default: m.ResponsableDashboardPage })));
const CompaniesPage                 = lazy(() => import('./pages/responsable/CompaniesPage').then(m => ({ default: m.CompaniesPage })));
const ResponsableStudentsPage       = lazy(() => import('./pages/responsable/StudentsPage').then(m => ({ default: m.ResponsableStudentsPage })));
const ResponsableInvitationsPage    = lazy(() => import('./pages/responsable/InvitationsPage').then(m => ({ default: m.ResponsableInvitationsPage })));
const ResponsableExportsPage        = lazy(() => import('./pages/responsable/ExportsPage').then(m => ({ default: m.ResponsableExportsPage })));
const ResponsableCandidaturesPage   = lazy(() => import('./pages/responsable/CandidaturesPage').then(m => ({ default: m.ResponsableCandidaturesPage })));
const ResponsableAnnoncesExternesPage = lazy(() => import('./pages/responsable/AnnoncesExternesPage').then(m => ({ default: m.ResponsableAnnoncesExternesPage })));
const ResponsableAidePage           = lazy(() => import('./pages/responsable/AidePage').then(m => ({ default: m.ResponsableAidePage })));

// Étudiant
const EtudiantDashboardPage  = lazy(() => import('./pages/etudiant/DashboardPage').then(m => ({ default: m.EtudiantDashboardPage })));
const EtudiantProfilPage     = lazy(() => import('./pages/etudiant/ProfilePage').then(m => ({ default: m.EtudiantProfilPage })));
const EtudiantNetworkPage    = lazy(() => import('./pages/etudiant/NetworkPage').then(m => ({ default: m.EtudiantNetworkPage })));
const EtudiantReferencesPage = lazy(() => import('./pages/etudiant/ReferencesPage').then(m => ({ default: m.EtudiantReferencesPage })));
const EtudiantImmersionsPage = lazy(() => import('./pages/etudiant/ImmersionsPage').then(m => ({ default: m.EtudiantImmersionsPage })));
const EtudiantEntretiensPage = lazy(() => import('./pages/etudiant/EntretiensPage').then(m => ({ default: m.EtudiantEntretiensPage })));
const EtudiantPublicationPage = lazy(() => import('./pages/etudiant/PublicationPage').then(m => ({ default: m.EtudiantPublicationPage })));
const EtudiantCvPage         = lazy(() => import('./pages/etudiant/CvPage').then(m => ({ default: m.EtudiantCvPage })));
const EtudiantQrCodePage     = lazy(() => import('./pages/etudiant/QrCodePage').then(m => ({ default: m.EtudiantQrCodePage })));
const EtudiantJobOffersPage      = lazy(() => import('./pages/etudiant/JobOffersPage').then(m => ({ default: m.EtudiantJobOffersPage })));
const EtudiantCandidaturesPage   = lazy(() => import('./pages/etudiant/CandidaturesPage').then(m => ({ default: m.EtudiantCandidaturesPage })));
const EtudiantAidePage           = lazy(() => import('./pages/etudiant/AidePage').then(m => ({ default: m.EtudiantAidePage })));

// Entreprise
const EntrepriseDashboardPage   = lazy(() => import('./pages/entreprise/DashboardPage').then(m => ({ default: m.EntrepriseDashboardPage })));
const EntrepriseSearchPage      = lazy(() => import('./pages/entreprise/SearchPage').then(m => ({ default: m.EntrepriseSearchPage })));
const EntrepriseCandidatPage    = lazy(() => import('./pages/entreprise/CandidatPage').then(m => ({ default: m.EntrepriseCandidatPage })));
const EntrepriseFavorisPage     = lazy(() => import('./pages/entreprise/FavorisPage').then(m => ({ default: m.EntrepriseFavorisPage })));
const EntrepriseRecherchesPage  = lazy(() => import('./pages/entreprise/RecherchesPage').then(m => ({ default: m.EntrepriseRecherchesPage })));
const EntrepriseJobOffersPage   = lazy(() => import('./pages/entreprise/JobOffersPage').then(m => ({ default: m.EntrepriseJobOffersPage })));
const EntrepriseBesoinsPage     = lazy(() => import('./pages/entreprise/BesoinsPage').then(m => ({ default: m.EntrepriseBesoinsPage })));
const EntrepriseBesoinDetailPage = lazy(() => import('./pages/entreprise/BesoinDetailPage').then(m => ({ default: m.EntrepriseBesoinDetailPage })));
const EntrepriseImmersionsPage  = lazy(() => import('./pages/entreprise/ImmersionsPage').then(m => ({ default: m.EntrepriseImmersionsPage })));
const EntrepriseEntretiensPage  = lazy(() => import('./pages/entreprise/EntretiensPage').then(m => ({ default: m.EntrepriseEntretiensPage })));
const EntrepriseProfilPage      = lazy(() => import('./pages/entreprise/ProfilPage').then(m => ({ default: m.EntrepriseProfilPage })));
const EntrepriseAidePage        = lazy(() => import('./pages/entreprise/AidePage').then(m => ({ default: m.EntrepriseAidePage })));
// V13 — CRM : espace candidatures entreprise
const EntrepriseCandidaturesPage = lazy(() => import('./pages/entreprise/CandidaturesPage').then(m => ({ default: m.EntrepriseCandidaturesPage })));

// Admin
const AdminDashboardPage        = lazy(() => import('./pages/admin/DashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const AdminUsersPage            = lazy(() => import('./pages/admin/UsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminUserCreatePage       = lazy(() => import('./pages/admin/UserCreatePage').then(m => ({ default: m.AdminUserCreatePage })));
const AdminUserDetailPage       = lazy(() => import('./pages/admin/UserDetailPage').then(m => ({ default: m.AdminUserDetailPage })));
const AdminAuditPage            = lazy(() => import('./pages/admin/AuditPage').then(m => ({ default: m.AdminAuditPage })));
const AdminReseauPage           = lazy(() => import('./pages/admin/ReseauPage').then(m => ({ default: m.AdminReseauPage })));
const AdminJobOffersPage        = lazy(() => import('./pages/admin/JobOffersPage').then(m => ({ default: m.AdminJobOffersPage })));
const ResponsableJobOffersPage  = lazy(() => import('./pages/admin/JobOffersPage').then(m => ({ default: m.ResponsableJobOffersPage })));
const AdminCandidaturesPage     = lazy(() => import('./pages/admin/CandidaturesPage').then(m => ({ default: m.AdminCandidaturesPage })));
const AdminConfigurationPage    = lazy(() => import('./pages/admin/ConfigurationPage').then(m => ({ default: m.AdminConfigurationPage })));
const AdminEstablishmentsPage   = lazy(() => import('./pages/admin/EstablishmentsPage').then(m => ({ default: m.AdminEstablishmentsPage })));
const AdminAiSettingsPage       = lazy(() => import('./pages/admin/AiSettingsPage').then(m => ({ default: m.AdminAiSettingsPage })));
const AdminObserverPage         = lazy(() => import('./pages/admin/ObserverPage').then(m => ({ default: m.AdminObserverPage })));
const AdminNavigationCacheePage = lazy(() => import('./pages/admin/NavigationCacheePage').then(m => ({ default: m.AdminNavigationCacheePage })));
const AdminGuideAlternancePage  = lazy(() => import('./pages/admin/GuideAlternancePage').then(m => ({ default: m.AdminGuideAlternancePage })));
const AdminRejectedOffersPage   = lazy(() => import('./pages/admin/RejectedOffersPage').then(m => ({ default: m.AdminRejectedOffersPage })));
const AdminDataRetentionPage    = lazy(() => import('./pages/admin/DataRetentionPage').then(m => ({ default: m.AdminDataRetentionPage })));
// V13 — CRM : vue d'ensemble recrutements (admin + responsable)
const TrackingOverviewPage      = lazy(() => import('./pages/admin/TrackingOverviewPage').then(m => ({ default: m.TrackingOverviewPage })));

// Étudiant — guide alternance (v11)
const EtudiantGuideAlternancePage = lazy(() => import('./pages/etudiant/GuideAlternancePage').then(m => ({ default: m.EtudiantGuideAlternancePage })));

// Compte
const CompteDonneesPage  = lazy(() => import('./pages/account/DataPage').then(m => ({ default: m.CompteDonneesPage })));
const CompteSecuritePage = lazy(() => import('./pages/account/SecurityPage').then(m => ({ default: m.CompteSecuritePage })));

// ─────────────────────────────────────────────────────────────────────────
// Fallback de chargement — affiché pendant le lazy load
// ─────────────────────────────────────────────────────────────────────────
function PageFallback() {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center p-8 text-muted-foreground text-sm">
      Chargement…
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageFallback />}>
        <Routes>
        <Route element={<PublicLayout />}>
          <Route index element={<LandingPage />} />
          <Route path="connexion" element={<LoginPage />} />
          <Route path="inscription-entreprise" element={<CompanyRegisterPage />} />
          <Route path="inscription-candidat" element={<RegisterCandidatePage />} />
          <Route path="invitation/:token" element={<InvitationPage />} />
          <Route path="reference/:token" element={<ReferencePublicPage />} />
          <Route path="reclamer-annonce/:token" element={<OrphanOfferClaimPage />} />
          <Route path="mot-de-passe-oublie" element={<ForgotPasswordPage />} />
          <Route path="reinitialiser-mdp" element={<ResetPasswordPage />} />
          <Route path="mentions-legales" element={<LegalPage kind="mentions" />} />
          <Route path="confidentialite" element={<LegalPage kind="confidentialite" />} />
          <Route path="cgu" element={<LegalPage kind="cgu" />} />
          <Route path="candidat/:id" element={<CandidatPublicPage />} />
        </Route>

        <Route
          path="responsable"
          element={
            <RequireRole roles={['responsable', 'super_admin']}>
              <ResponsableLayout />
            </RequireRole>
          }
        >
          <Route index element={<ResponsableDashboardPage />} />
          <Route path="entreprises" element={<CompaniesPage />} />
          <Route path="etudiants" element={<ResponsableStudentsPage />} />
          <Route path="annonces" element={<ResponsableJobOffersPage />} />
          <Route path="annonces-externes" element={<ResponsableAnnoncesExternesPage />} />
          <Route path="candidatures" element={<ResponsableCandidaturesPage />} />
          <Route path="invitations" element={<ResponsableInvitationsPage />} />
          <Route path="exports" element={<ResponsableExportsPage />} />
          <Route path="aide" element={<ResponsableAidePage />} />
          {/* V13 — CRM : tableau de bord des recrutements */}
          <Route path="suivi-recrutements" element={<TrackingOverviewPage />} />
        </Route>

        <Route
          path="etudiant"
          element={
            <RequireRole roles={['student']}>
              <EtudiantLayout />
            </RequireRole>
          }
        >
          <Route index element={<EtudiantDashboardPage />} />
          <Route path="profil" element={<EtudiantProfilPage />} />
          {/* Les secteurs sont désormais un onglet du profil — rediriger les
              anciens bookmarks vers /etudiant/profil. */}
          <Route path="secteurs" element={<Navigate to="/etudiant/profil" replace />} />
          <Route path="cv" element={<EtudiantCvPage />} />
          <Route path="references" element={<EtudiantReferencesPage />} />
          <Route path="immersions" element={<EtudiantImmersionsPage />} />
          <Route path="entretiens" element={<EtudiantEntretiensPage />} />
          <Route path="reseau" element={<EtudiantNetworkPage />} />
          <Route path="annonces" element={<EtudiantJobOffersPage />} />
          <Route path="candidatures" element={<EtudiantCandidaturesPage />} />
          <Route path="publication" element={<EtudiantPublicationPage />} />
          <Route path="qr-code" element={<EtudiantQrCodePage />} />
          <Route path="aide" element={<EtudiantAidePage />} />
          {/* V11 — guide alternance (lecture étudiant, visibilité vérifiée backend) */}
          <Route path="guide-alternance" element={<EtudiantGuideAlternancePage />} />
        </Route>

        <Route
          path="entreprise"
          element={
            <RequireRole roles={['company', 'super_admin']}>
              <EntrepriseLayout />
            </RequireRole>
          }
        >
          <Route index element={<EntrepriseDashboardPage />} />
          <Route path="recherche" element={<EntrepriseSearchPage />} />
          <Route path="candidat/:id" element={<EntrepriseCandidatPage />} />
          <Route path="favoris" element={<EntrepriseFavorisPage />} />
          <Route path="recherches" element={<EntrepriseRecherchesPage />} />
          <Route path="annonces" element={<EntrepriseJobOffersPage />} />
          <Route path="besoins" element={<EntrepriseBesoinsPage />} />
          <Route path="besoins/:id" element={<EntrepriseBesoinDetailPage />} />
          <Route path="immersions" element={<EntrepriseImmersionsPage />} />
          <Route path="entretiens" element={<EntrepriseEntretiensPage />} />
          <Route path="profil" element={<EntrepriseProfilPage />} />
          <Route path="aide" element={<EntrepriseAidePage />} />
          {/* V13 — CRM recrutement */}
          <Route path="candidatures" element={<EntrepriseCandidaturesPage />} />
        </Route>

        <Route
          path="admin"
          element={
            <RequireRole roles={['super_admin']}>
              <AdminLayout />
            </RequireRole>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="utilisateurs" element={<AdminUsersPage />} />
          <Route path="utilisateurs/nouveau" element={<AdminUserCreatePage />} />
          <Route path="utilisateurs/:id" element={<AdminUserDetailPage />} />
          <Route path="journal" element={<AdminAuditPage />} />
          <Route path="reseau" element={<AdminReseauPage />} />
          <Route path="annonces" element={<AdminJobOffersPage />} />
          <Route path="candidatures" element={<AdminCandidaturesPage />} />
          <Route path="etablissements" element={<AdminEstablishmentsPage />} />
          <Route path="ia" element={<AdminAiSettingsPage />} />
          <Route path="configuration" element={<AdminConfigurationPage />} />
          <Route path="navigation-cachee" element={<AdminNavigationCacheePage />} />
          {/* V8 — vue admin des annonces externes : on ré-utilise le
              composant responsable, le backend monte la même handler
              sous /admin/external-offers et /responsable/external-offers. */}
          <Route path="annonces-externes" element={<ResponsableAnnoncesExternesPage />} />
          {/* V11 — guide alternance (éditeur super_admin) */}
          <Route path="guide-alternance" element={<AdminGuideAlternancePage />} />
          {/* V12 — annonces externes rejetées (super_admin uniquement) */}
          <Route path="annonces-rejetees" element={<AdminRejectedOffersPage />} />
          {/* RGPD — rétention des données (purge RGPD Art. 5.1.e) */}
          <Route path="retention-donnees" element={<AdminDataRetentionPage />} />
          {/* V13 — CRM : tableau de bord des recrutements */}
          <Route path="suivi-recrutements" element={<TrackingOverviewPage />} />
          {/* NOTE: /admin/observer est déclaré EN DEHORS de ce bloc (voir bas
              du composant) pour éviter le bootstrap circulaire : RequireRole
              appelle /auth/me avant que sessionStorage ait le token. */}
        </Route>

        <Route
          path="compte"
          element={
            <RequireRole roles={['student', 'company', 'responsable', 'super_admin']}>
              <CompteLayout />
            </RequireRole>
          }
        >
          <Route path="donnees" element={<CompteDonneesPage />} />
          <Route path="securite" element={<CompteSecuritePage />} />
        </Route>

        {/*
         * Route /admin/observer intentionnellement HORS du bloc RequireRole.
         * Raison : si elle était protégée par RequireRole('super_admin'),
         * ce garde appellerait /auth/me SANS le token (sessionStorage vide au
         * premier rendu), mettrait en cache role='super_admin', puis RequireRole
         * sur /etudiant bloquerait (cache stale 60s) → redirection silencieuse.
         * Sécurité : redirige vers /admin si token absent, HMAC validé backend.
         */}
        <Route path="admin/observer" element={<AdminObserverPage />} />

          <Route path="*" element={<PublicLayout />}>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
